import React, { createContext, useContext, useEffect, useState } from 'react';

export interface CartItem {
  product_id: string | number;
  name: string;
  price: number;
  quantity: number;
}

type CartContextValue = {
  items: CartItem[];
  add: (item: CartItem) => void;
  remove: (product_id: string | number) => void;
  updateQty: (product_id: string | number, qty: number) => void;
  clear: () => void;
  total: () => number;
};

const CartContext = createContext<CartContextValue | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>(() => {
    try {
      const raw = localStorage.getItem('cart');
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });

  useEffect(() => { localStorage.setItem('cart', JSON.stringify(items)); }, [items]);

  const add = (it: CartItem) => {
    setItems(cur => {
      const itemToAdd = { ...it, price: Number(it.price), quantity: Number(it.quantity) };
      const idx = cur.findIndex(c => String(c.product_id) === String(itemToAdd.product_id));
      if (idx >= 0) {
        const copy = [...cur]; copy[idx].quantity = Number(copy[idx].quantity) + Number(itemToAdd.quantity); return copy;
      }
      return [...cur, itemToAdd];
    });
  };
  const remove = (product_id: string | number) => setItems(cur => cur.filter(i => String(i.product_id) !== String(product_id)));
  const updateQty = (product_id: string | number, qty: number) => setItems(cur => cur.map(i => String(i.product_id) === String(product_id) ? { ...i, quantity: qty } : i));
  const clear = () => setItems([]);
  const total = () => items.reduce((s, i) => s + i.price * i.quantity, 0);

  return <CartContext.Provider value={{ items, add, remove, updateQty, clear, total }}>{children}</CartContext.Provider>;
};

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}

export default CartContext;
